var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var _a;
import * as typesVMinus1 from "./types-versions/types.v-1";
import * as typesV0 from "./types-versions/types.v0";
import * as typesV1 from "./types-versions/types.v1";
import * as typesV7 from "./types-versions/types.v7";
import { defaultOvalGripPositionDiameterRatio, objectPosition, defaultCardboardBoxId, defaultPalletSize, } from "3d/constants/common";
import { tubeDiameterOptions, UnitSystem } from "store/ui/types";
/*
 * Each migration step will take one version as input and return the next version as output.
 * (The key `2` means that it is the step which migrates from V1 to V2.)
 */
var migrations = (_a = {},
    _a[0] = function (state) {
        /* Example: add a new (required) key, and change the type of an existing key for all nodes in an old state */
        var newNodes = Object.values(state.nodes).reduce(function (nodes, currentNode) {
            var nodeV0 = __assign(__assign({}, currentNode), { rotationY: Number(currentNode.rotationY), position: { x: 0, y: 5.5, z: 0 } });
            nodes[currentNode.id] = nodeV0;
            return nodes;
        }, {});
        return __assign(__assign({}, state), { nodes: newNodes });
    },
    // v1: add tube type to attachment and tube length/diameter to grip position
    _a[1] = function (state) {
        var newAttachments = Object.values(state.attachments).reduce(function (attachments, currentAtt) {
            attachments[currentAtt.id] = __assign(__assign({}, currentAtt), { tubeType: typesV1.TubeType.UNKNOWN });
            return attachments;
        }, {});
        var newNodes = Object.values(state.nodes).reduce(function (nodes, currentNode) {
            if (currentNode.type === typesV0.NodeComponentType.GRIP_POSITION) {
                var gp = currentNode.component;
                var v1Gp = __assign(__assign({}, gp), { inlineTubeLength: 0.1, inlineTubeDiameter: 0.004 });
                nodes[currentNode.id] = __assign(__assign({}, currentNode), { component: v1Gp });
            }
            else {
                nodes[currentNode.id] = currentNode;
            }
            return nodes;
        }, {});
        return __assign(__assign({}, state), { attachments: newAttachments, nodes: newNodes });
    },
    _a[2] = function (state) {
        var newEdges = Object.values(state.edges).reduce(function (edges, currentEdge) {
            edges[currentEdge.id] = __assign(__assign({}, currentEdge), { defaultDiameterIndex: 2, blowOffDiameterIndex: 2 });
            return edges;
        }, {});
        var newObjectShape = state.objectShape;
        return __assign(__assign({}, state), { edges: newEdges, objectShape: newObjectShape });
    },
    _a[3] = function (state) {
        var newEdges = Object.values(state.edges).reduce(function (edges, currentEdge) {
            // Removes lengthLocked property and copies remaining properties into newEdge
            var lengthLocked = currentEdge.lengthLocked, newEdge = __rest(currentEdge, ["lengthLocked"]);
            edges[currentEdge.id] = newEdge;
            return edges;
        }, {});
        var newNodes = Object.values(state.nodes).reduce(function (nodes, currentNode) {
            // Looks for nodes that are tube connections
            if (currentNode.type === typesVMinus1.NodeComponentType.TUBE_CONNECTION) {
                // Removes diameter property and copies remaining properties into newTubeConnection
                var _a = currentNode.component, diameter = _a.diameter, newTubeConnection = __rest(_a, ["diameter"]);
                // Save the old diameter property in the new diameters property
                newTubeConnection.diameters = [diameter];
                // Save the new component on current node
                currentNode.component = __assign({}, newTubeConnection);
            }
            nodes[currentNode.id] = currentNode;
            return nodes;
        }, {});
        return __assign(__assign({}, state), { edges: newEdges, nodes: newNodes, hasDisconnectedEdges: true });
    },
    _a[4] = function (state) {
        var newEdges = Object.values(state.edges).reduce(function (edges, currentEdge) {
            edges[currentEdge.id] = __assign(__assign({}, currentEdge), { lengthLocked: false });
            return edges;
        }, {});
        return __assign(__assign({}, state), { edges: newEdges });
    },
    _a[5] = function (state) {
        return {
            past: [],
            present: __assign({}, state),
            future: [],
        };
    },
    _a[6] = function (state) {
        var presentState = state.present;
        var newObjectShape = presentState.objectShape;
        return __assign(__assign({}, state), { present: __assign(__assign({}, presentState), { objectShape: newObjectShape }) });
    },
    _a[7] = function (state) {
        return state;
    },
    _a[8] = function (state) {
        Object.values(state.present.nodes)
            .filter(function (node) { return node.type === typesV7.NodeComponentType.GRIP_POSITION; })
            .forEach(function (node) {
            var gripPosition = node.component;
            // set grip position height if missing
            if (gripPosition.height === undefined) {
                var isOval = gripPosition.marker === typesVMinus1.GripPositionMarkerId.OVAL;
                gripPosition.height = isOval
                    ? gripPosition.width / defaultOvalGripPositionDiameterRatio
                    : gripPosition.width;
            }
        });
        return state;
    },
    // v9: fix tube diameter indexes that were shifted when 7 new sizes were prepended
    // to tubeDiameterOptions in both METRIC and IMPERIAL arrays. Uses the stored
    // diameter values (diameterDefaultTube / diameterBlowOffTube) as source of truth
    // to find the correct new index, making this safe for both pre- and post-shift saves.
    _a[9] = function (state) {
        var findNewIndex = function (diameterValue) {
            var metricIndex = tubeDiameterOptions[UnitSystem.METRIC].findIndex(function (o) { return o.value === diameterValue; });
            if (metricIndex !== -1)
                return metricIndex;
            var imperialIndex = tubeDiameterOptions[UnitSystem.IMPERIAL].findIndex(function (o) { return o.value === diameterValue; });
            return imperialIndex !== -1 ? imperialIndex : undefined;
        };
        Object.values(state.present.edges).forEach(function (edge) {
            if (edge.defaultDiameterIndex !== undefined) {
                edge.defaultDiameterIndex = findNewIndex(edge.diameterDefaultTube);
            }
            if (edge.blowOffDiameterIndex !== undefined) {
                edge.blowOffDiameterIndex = findNewIndex(edge.diameterBlowOffTube);
            }
        });
        return state;
    },
    // v10: the multiple-boxes feature — placed cardboard boxes (with rotation
    // and stacking layer), the shared pallet footprint, the palletizing
    // pattern per layer, and the per-system switch that reveals it all.
    // The remembered last tube connection type is dropped: a new tube
    // connection is a placeholder until the user picks its type.
    // Existing (single-object) systems are unchanged in what they show: they
    // get one unrotated box on layer 0 at the previous fixed object position,
    // the default pallet, both layers marked "custom" (null pattern) so a
    // hand-built arrangement is never laid out again behind the user's back
    // when a size changes, and the switch off — a pre-v10 system can never
    // have visibly used the feature. The same seed is applied to every history
    // snapshot (present/past/future/_latestUnfiltered) so undo/redo can never
    // restore a box-less state.
    _a[10] = function (state) {
        var _a, _b;
        var seed = function (_a) {
            var latestSelectedConnectionType = _a.latestSelectedConnectionType, scene = __rest(_a, ["latestSelectedConnectionType"]);
            return (__assign(__assign({}, scene), { cardboardBoxes: [
                    {
                        id: defaultCardboardBoxId,
                        position: { x: objectPosition.x, z: objectPosition.z },
                        rotation: 0,
                        layer: 0,
                    },
                ], pallet: __assign({}, defaultPalletSize), layerPatterns: { 0: null, 1: null }, multipleBoxesEnabled: false }));
        };
        /* `past` and `future` are blacklisted from persistence, so a stored
         * scene (a reload, a saved system, a predefined system template) never
         * carries them — only the in-memory history does. */
        return __assign(__assign({}, state), { present: seed(state.present), past: ((_a = state.past) !== null && _a !== void 0 ? _a : []).map(seed), future: ((_b = state.future) !== null && _b !== void 0 ? _b : []).map(seed), _latestUnfiltered: state._latestUnfiltered
                ? seed(state._latestUnfiltered)
                : state._latestUnfiltered });
    },
    _a);
export { migrations };
